import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from './api.ts';

/** Quantos avisos não lidos a pessoa tem (consulta a cada minuto). */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
}

/** Sino da central de avisos: leva a "Minhas pendências" e mostra quantos avisos não foram lidos. */
export function NotificationBell({ variant }: { variant: 'rail' | 'header' | 'sidebar' }) {
  const { t } = useTranslation();
  const count = useUnreadCount().data?.count ?? 0;
  const label = count > 0 ? t('notifications.bellUnread', { count }) : t('notifications.bell');
  const badge = count > 0 && (
    <span className="absolute -top-1 -right-1 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] leading-4 font-semibold text-sheet">
      {count > 99 ? '99+' : count}
    </span>
  );
  if (variant === 'header') {
    return (
      <Link
        to="/notifications"
        aria-label={label}
        title={label}
        className="relative p-1.5 text-ink"
      >
        <Bell size={22} aria-hidden />
        {badge}
      </Link>
    );
  }
  if (variant === 'sidebar') {
    // Rodapé da lateral única: o sino com o nome e quantos avisos não foram lidos.
    return (
      <Link
        to="/notifications"
        aria-label={label}
        className="flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-ink hover:bg-paper"
      >
        <span className="relative text-muted">
          <Bell size={18} strokeWidth={1.75} aria-hidden />
          {badge}
        </span>
        {t('notifications.short')}
      </Link>
    );
  }
  // Lateral recolhida: só o ícone, com o nome na dica e para o leitor de tela.
  return (
    <Link
      to="/notifications"
      aria-label={label}
      title={label}
      className="flex size-10 items-center justify-center rounded-md text-rail-text hover:bg-paper hover:text-ink"
    >
      <span className="relative">
        <Bell size={20} strokeWidth={1.75} aria-hidden />
        {badge}
      </span>
    </Link>
  );
}
